import React, { useMemo } from 'react';

import BedroomParentIcon from '@mui/icons-material/BedroomParent';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TourIcon from '@mui/icons-material/Tour';

import {
  ROLE_ADMIN_USER,
  ROLE_CRUISE_COORDINATOR,
  ROLE_EMPLOYEE_USER,
  ROLE_EXPERIENCES_COORDINATOR,
  ROLE_HOTEL_COORDINATOR,
  ROLE_TOUR_COORDINATOR,
} from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import { intersection } from '~/utils/sets';

import NavDropdown from './NavDropdown';

const ProductsMenu = [
  {
    label: 'Offers',
    href: '/offers',
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_HOTEL_COORDINATOR]),
    icon: <HotelIcon />,
  },
  {
    label: 'Bedbank',
    href: '/bedbank/properties',
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_HOTEL_COORDINATOR]),
    icon: <BedroomParentIcon />,
  },
  {
    label: 'Tours',
    href: '/tours',
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_TOUR_COORDINATOR]),
    icon: <TourIcon />,
  },
  {
    label: 'Flights',
    href: '/flights',
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
    icon: <FlightIcon />,
  },
  {
    label: 'Experiences',
    href: '/experiences',
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EXPERIENCES_COORDINATOR]),
    icon: <LocalActivityIcon />,
  },
  {
    label: 'Corporate Events',
    href: '/corporate-events/events',
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
    icon: <BusinessIcon />,
  },
  {
    label: 'Cruises',
    href: '/cruises/offers',
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_CRUISE_COORDINATOR]),
    icon: <DirectionsBoatIcon />,
  },
  {
    label: 'Shop',
    href: '/shop',
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
    icon: <StorefrontIcon />,
  },
  {
    label: 'TripPlanner',
    href: '/trip-planner',
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_EXPERIENCES_COORDINATOR]),
    icon: <FavoriteIcon />,
  },
] as const;

export default function ProductsDropdown() {
  const { user: currentUser } = useCurrentUser();

  const currentUserRoles = useMemo(() => (currentUser ? new Set(currentUser.roles) : new Set()), [currentUser]);
  const links = useMemo(
    () => ProductsMenu.filter((item) => intersection(currentUserRoles, item.requiredRoles ?? new Set()).size),
    [currentUserRoles],
  );

  if (!currentUser) {
    return null;
  }

  return <NavDropdown buttonLabel="Products" items={links} />;
}
